import { useEffect, useState, useRef } from 'react';
import { Input, Space, Table } from 'antd';
import { useDispatch } from 'react-redux';
import debounce from 'lodash.debounce';

function TableComponent({ listData, columns, placeholder, id, callbackFun, showSearch = true, dateFilter, providerFilter, onTotalAmountChange, totalAmount, showTotalAmount }) {
    const dispatch = useDispatch();
    const [data, setData] = useState();
    const [total, setTotal] = useState(0);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [search, setSearch] = useState("");
    const prevDateFilterRef = useRef();
    const prevProviderFilterRef = useRef();

    useEffect(() => {
        const dateFilterChanged = prevDateFilterRef.current !== dateFilter;
        if (dateFilterChanged && dateFilter !== undefined) {
            setPagination((prev) => ({ ...prev, current: 1 }));
        }
        prevDateFilterRef.current = dateFilter;
    }, [dateFilter]);

    useEffect(() => {
        const providerFilterChanged = prevProviderFilterRef.current !== providerFilter;
        if (providerFilterChanged) {
            setPagination((prev) => ({ ...prev, current: 1 }));
        }
        prevProviderFilterRef.current = providerFilter;
    }, [providerFilter]);

    useEffect(() => {
        getData(pagination.current, pagination.pageSize, search, dateFilter, providerFilter)
    }, [pagination, search, dateFilter, providerFilter]);

    const getData = async (page = 1, limit = 10, search = '', dateFilter = null, providerFilter = null) => {
        let data = {
            page: page,
            limit
        };
        if (id) {
            data = {
                ...data,
                id
            }
        }

        if (search.trim() !== "") {
            data = {
                ...data,
                search
            }
        }

        if (dateFilter && dateFilter.length === 2 && dateFilter[0] && dateFilter[1]) {
            data = {
                ...data,
                start_date: dateFilter[0].format('YYYY-MM-DD'),
                end_date: dateFilter[1].format('YYYY-MM-DD')
            }
        }

        if (providerFilter) {
            data = {
                ...data,
                provider_id: providerFilter
            }
        }
        setIsLoading(true)

        const result = await dispatch(listData(data));

        if (listData.fulfilled.match(result)) {
            const response = result?.payload;
            if (typeof callbackFun == "function") {
                callbackFun(response?.service_name)
            }
            const transactionData = response?.data || [];
            setData(transactionData);
            setTotal(response?.total);

            // Calculate total amount from current page data
            if (onTotalAmountChange) {
                const totalAmount = transactionData.reduce((sum, item) => {
                    const amount = parseFloat(item.amount) || 0;
                    return sum + amount;
                }, 0);
                onTotalAmountChange(totalAmount);
            }

            setIsLoading(false)
        } else {
            setIsLoading(false)
            if (onTotalAmountChange) {
                onTotalAmountChange(0);
            }
            console.error("Failed to fetch data");
        }
    };

    const handleTableChange = (paginationInfo) => {
        setPagination({
            current: paginationInfo.current,
            pageSize: paginationInfo.pageSize,
        });
    };

    const handleSearch = debounce((value) => {
        setPagination((prev) => ({ ...prev, current: 1 }));
        setSearch(value);
    }, 500);

    return (
        <>
            {showSearch && (
                <Space style={{ marginBottom: 16 }}>
                    <Input.Search
                        placeholder={placeholder}
                        onChange={(e) => handleSearch(e.target.value)}
                        allowClear
                        style={{ width: 300 }}
                    />
                </Space>
            )}
            <Table dataSource={data} columns={columns}
                loading={isLoading}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} items`,
                }}
                onChange={handleTableChange}
                footer={showTotalAmount && totalAmount !== undefined ? () => (
                    <div style={{ padding: '12px 16px', backgroundColor: '#f5f5f5', borderRadius: 4, textAlign: 'right', fontWeight: 'bold', fontSize: '16px' }}>
                        Total Amount: ${totalAmount.toFixed(2)}
                    </div>
                ) : null}
            />
        </>
    );
}

export default TableComponent;